/* eslint-disable radix */
/* eslint-disable arrow-body-style */
/* eslint-disable react/prop-types */
// @mui
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { Box, Paper, Tooltip, Typography, TextField } from '@mui/material';
import '../../../app.css';
import dayjs from 'dayjs';

export default function Calendar({ date, setDate, setMonth, monthlyStat, runningTask }) {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper className="timelineCalendar" sx={{ overflow: 'hidden' }}>
        <StaticDatePicker
          // mask='____/__/__'
          variant="static"
          orientation="portrait"
          value={date}
          disableFuture
          renderInput={(params) => <TextField {...params} />}
          componentsProps={{
            actionBar: {
              actions: [],
            },
          }}
          ToolbarComponent={(params) => <MyToolbar {...params} />}
          onChange={(newValue) => setDate(newValue)}
          onMonthChange={(month) => setMonth(month)}
          renderDay={(day, _value, DayComponentProps) => (
            <DayComponent
              key={dayjs(day).format('YYYY-MM-DD')}
              day={day}
              monthlyStat={monthlyStat}
              runningTask={runningTask}
              DayComponentProps={DayComponentProps}
            />
          )}
        />
        <Box sx={{ px: 2, pb: 2, pt: 1 }}>
          <Typography variant="caption" sx={{ color: '#637381', fontWeight: 600 }}>
            Color Guide
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <LegendItem color="#FF9800" label="Half-day Leave" />
            <LegendItem color="#F44336" label="Full-day Leave / Less than 4h" />
            <LegendItem color="#ccff90" borderColor="#4CAF50" label="Completed Min Time" />
            <LegendItem color="#D3D3D3" label="Weekend" />
            <LegendItem color="#800080" label="Office Holiday" />
          </Box>
        </Box>
      </Paper>
    </LocalizationProvider>
  );
}

const MyToolbar = (props) => {
  return (
    <Box sx={{ my: 1, mx: 3 }}>
      <Typography variant="overline" sx={{ color: '#637381' }}>
        Select Date
      </Typography>
      <Typography variant="h4">{dayjs(props?.parsedValue)?.format('MMM DD, YYYY')}</Typography>
    </Box>
  );
};

const LegendItem = ({ color, borderColor = 'rgba(0,0,0,0.15)', label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: color,
        border: `1px solid ${borderColor}`,
      }}
    />
    <Typography variant="caption" sx={{ color: '#161C24' }}>
      {label}
    </Typography>
  </Box>
);

const DayComponent = ({ day, monthlyStat, runningTask, DayComponentProps }) => {
  // Avoid spreading `key` into JSX props; React warns for this.
  const { key: dayKey, ...pickersDayProps } = DayComponentProps || {};

  if (DayComponentProps?.outsideCurrentMonth) {
    return <PickersDay key={dayKey} {...pickersDayProps} />;
  }

  const dateStr = dayjs(day).format('YYYY-MM-DD');
  const result = (monthlyStat || []).find((data) => {
    const rawDate = data?.Date;
    if (!rawDate) return false;
    // Normalize in case `monthlyStat.Date` isn't already `YYYY-MM-DD`
    return dayjs(rawDate).format('YYYY-MM-DD') === dateStr;
  });
  const isRunning =
    runningTask && new Date(runningTask?.TimeTracker?.[0]?.startTime).toDateString() === new Date(day).toDateString();
  const leave = result?.leave || null;
  const isWeekend = [0, 6].includes(new Date(day).getDay()); // Sunday=0, Saturday=6
  const isOfficeHoliday = Boolean(result?.officeHoliday);
  const officeHolidayName = result?.officeHolidayName || 'Office Holiday';

  const hasTimerData = Boolean(parseInt(result?.total_time) || isRunning);
  const hasLeave = Boolean(leave);
  if (!hasTimerData && !isWeekend && !isOfficeHoliday && !hasLeave) {
    return <PickersDay key={dayKey} {...pickersDayProps} />;
  }

  // `monthlyStat.total_time` already includes active timers from backend month API.
  // Do not add running-task duration again, or tooltip gets double-counted.
  const totalTime = parseInt(result?.total_time) || 0;
  const totalHour = Math.floor(totalTime / (60 * 60));
  const totalMinute = Math.floor((totalTime % (60 * 60)) / 60);
  const workedHours = totalTime / (60 * 60);
  const workPercentage = Math.min(100, Math.max(0, (totalTime / (8 * 60 * 60)) * 100));

  // Priority: holiday > leave > weekend-without-work > timer buckets
  // If there is worked time on weekends, show timer colors instead of gray weekend.
  let bgColor = '#4CAF50'; // >= 6h : green
  let tooltipTitle = `${totalHour}h ${totalMinute}m`;
  if (isOfficeHoliday) {
    bgColor = '#800080'; // purple
    tooltipTitle = officeHolidayName;
  } else if (hasLeave) {
    if (leave.isHalfDay) {
      bgColor = '#FF9800'; // orange
      tooltipTitle = 'Half-day Leave';
    } else {
      bgColor = '#F44336'; // red
      tooltipTitle = 'Full-day Leave';
    }
  } else if (isWeekend && !hasTimerData) {
    bgColor = '#D3D3D3'; // gray
    tooltipTitle = 'Weekend';
  } else if (hasTimerData) {
    if (workedHours < 4) {
      bgColor = '#F44336'; // red
    } else if (workedHours < 6) {
      bgColor = '#FF9800'; // orange
    } else {
      // bgColor = '#4CAF50'; // green
      bgColor = '#ccff90'; // green
    }
  }

  const useWhiteText = ['#F44336', '#FF9800', '#800080'].includes(bgColor);
  // Keep white progress mask until 8h so fill progresses even in green range (6h-8h).
  // At >= 8h the circle becomes fully green.
  const showProgressOverlay =
    hasTimerData &&
    !hasLeave &&
    !isWeekend &&
    !isOfficeHoliday &&
    workedHours < 8;

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow>
      <div
        style={{
          background: bgColor || "#ccff90",
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          border: bgColor === '#ccff90' ? '1.5px solid #66BB6A' : 'none',
          boxShadow: bgColor === '#ccff90' ? 'inset 0 0 0 1px rgba(102,187,106,0.35)' : 'none',
          position: 'relative',
          margin: '2px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 5,
        }}
      >
        <PickersDay
          key={dayKey}
          {...pickersDayProps}
          sx={{
            zIndex: 2,
            opacity: 1,
            bgcolor: 'transparent',
            position: 'absolute',
            color: useWhiteText ? '#FFFFFF' : 'black',
            '&.Mui-selected': {
              bgcolor: 'transparent',
              color: '#FFFFFF',
            },
            '&.Mui-selected:hover': {
              bgcolor: 'transparent',
              color: '#FFFFFF',
            },
          }}
        />
        {showProgressOverlay && (
          <div
            style={{
              position: 'absolute',
              width: '36px',
              top: '0',
              left: '0',
              height: `${100 - workPercentage}%`,
              background: 'white',
              zIndex: 1,
            }}
          />
        )}
      </div>
    </Tooltip>
  );
};
